import React, { useState, useRef, useCallback } from 'react';
import {
  Box, Typography, Button, Paper, Chip, Tooltip, useTheme, alpha, Alert, Dialog,
  DialogTitle, DialogContent, DialogContentText, DialogActions,
} from '@mui/material';
import {
  PlayArrow as PlayIcon, Pause as PauseIcon, Stop as StopIcon, Replay as RetryIcon,
  ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, CheckCircle as CheckIcon,
  Error as ErrorIcon, HourglassEmpty as PendingIcon, Sync as RunningIcon,
  Description as ContractIcon, DeleteForever as ResetIcon,
} from '@mui/icons-material';
import { ALL_TEST_STEPS, PHASE_NAMES, TOTAL_PHASES } from './testSteps';
import { validateContracts } from './contractValidator';
import { authService } from '../../services/authService';
import api, { startCapturing, stopCapturing } from '../../services/api';
import './ApiHealthPage.css';

const STEP_DELAY_MS = 3000;

const STATUS = { IDLE: 'idle', RUNNING: 'running', PAUSED: 'paused', DONE: 'done', STOPPED: 'stopped' };
const STEP_STATUS = { PENDING: 'pending', RUNNING: 'running', SUCCESS: 'success', FAIL: 'fail' };

function getStatusIcon(status) {
  switch (status) {
    case STEP_STATUS.SUCCESS: return <CheckIcon sx={{ color: 'success.main', fontSize: 18 }} />;
    case STEP_STATUS.FAIL: return <ErrorIcon sx={{ color: 'error.main', fontSize: 18 }} />;
    case STEP_STATUS.RUNNING: return <span className="spinning"><RunningIcon sx={{ fontSize: 18, color: 'info.main' }} /></span>;
    default: return <PendingIcon sx={{ color: 'text.disabled', fontSize: 18 }} />;
  }
}

const makeInitialResults = () => ALL_TEST_STEPS.map(s => ({
  id: s.id, status: STEP_STATUS.PENDING, errors: [], responseData: null, requestData: null, duration: null,
}));

export default function ApiHealthPage() {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState('tests');
  const [runStatus, setRunStatus] = useState(STATUS.IDLE);
  const [stepResults, setStepResults] = useState(makeInitialResults);
  const [failedAtIndex, setFailedAtIndex] = useState(null);
  const [expandedPhases, setExpandedPhases] = useState(() => {
    const map = {};
    for (let i = 0; i < TOTAL_PHASES; i++) map[i] = true;
    return map;
  });
  const [expandedResponses, setExpandedResponses] = useState({});
  const [expandedRequests, setExpandedRequests] = useState({});
  const [contractResults, setContractResults] = useState(null);
  const [contractLoading, setContractLoading] = useState(false);
  const [contractError, setContractError] = useState(null);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState(null);

  const ctxRef = useRef({});
  const pauseRef = useRef(false);
  const stopRef = useRef(false);

  const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  const updateStepResult = useCallback((index, updates) => {
    setStepResults(prev => {
      const next = [...prev];
      next[index] = { ...next[index], ...updates };
      return next;
    });
  }, []);

  const runStep = useCallback(async (index) => {
    const step = ALL_TEST_STEPS[index];
    const startTime = Date.now();
    updateStepResult(index, { status: STEP_STATUS.RUNNING, errors: [], responseData: null, requestData: null, duration: null });
    startCapturing();

    try {
      const data = await step.run(ctxRef.current);
      const requests = stopCapturing();
      const duration = Date.now() - startTime;
      const validation = step.validate(data, ctxRef.current);
      updateStepResult(index, {
        status: validation.valid ? STEP_STATUS.SUCCESS : STEP_STATUS.FAIL,
        errors: validation.errors, responseData: data, requestData: requests, duration,
      });
      return validation.valid;
    } catch (err) {
      const requests = stopCapturing();
      const duration = Date.now() - startTime;
      const errorMsg = err.response?.data?.message || err.response?.data?.title || err.message || 'Erro desconhecido';
      const statusCode = err.response?.status ? ` (HTTP ${err.response.status})` : '';
      updateStepResult(index, {
        status: STEP_STATUS.FAIL,
        errors: [`${errorMsg}${statusCode}`],
        responseData: err.response?.data || null, requestData: requests, duration,
      });
      return false;
    }
  }, [updateStepResult]);

  const runAllSteps = useCallback(async (startFrom = 0) => {
    setRunStatus(STATUS.RUNNING);
    setFailedAtIndex(null);
    stopRef.current = false;
    pauseRef.current = false;

    for (let i = startFrom; i < ALL_TEST_STEPS.length; i++) {
      if (stopRef.current) { setRunStatus(STATUS.IDLE); return; }

      while (pauseRef.current) {
        setRunStatus(STATUS.PAUSED);
        await delay(200);
        if (stopRef.current) { setRunStatus(STATUS.IDLE); return; }
      }

      setRunStatus(STATUS.RUNNING);
      const success = await runStep(i);

      if (!success) {
        // PARA na falha
        setFailedAtIndex(i);
        setRunStatus(STATUS.STOPPED);
        return;
      }

      if (i < ALL_TEST_STEPS.length - 1) await delay(STEP_DELAY_MS);
    }

    setRunStatus(STATUS.DONE);
  }, [runStep]);

  const handleStart = () => {
    ctxRef.current = {};
    setStepResults(makeInitialResults());
    setExpandedResponses({});
    setExpandedRequests({});
    setFailedAtIndex(null);
    runAllSteps(0);
  };

  const handlePause = () => { pauseRef.current = true; };
  const handleResume = () => { pauseRef.current = false; };
  const handleStop = () => { stopRef.current = true; pauseRef.current = false; };

  const handleRetryStep = async (index) => {
    const success = await runStep(index);
    if (success) runAllSteps(index + 1);
  };

  const handleContinueFromStep = (index) => { runAllSteps(index); };

  const togglePhase = (phase) => setExpandedPhases(prev => ({ ...prev, [phase]: !prev[phase] }));
  const toggleResponse = (stepId) => setExpandedResponses(prev => ({ ...prev, [stepId]: !prev[stepId] }));
  const toggleRequest = (stepId) => setExpandedRequests(prev => ({ ...prev, [stepId]: !prev[stepId] }));

  // --- Reset user ---
  const handleResetUser = async () => {
    setResetLoading(true);
    setResetError(null);
    try {
      await api.post('/dev/reset-user');
      setResetDialogOpen(false);
      authService.removeToken();
      authService.redirectToLogin();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Erro ao resetar usuário';
      setResetError(msg);
    } finally {
      setResetLoading(false);
    }
  };

  // --- Contract validation ---
  const handleLoadContracts = async () => {
    setContractLoading(true);
    setContractError(null);
    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';
      const swaggerUrl = API_BASE_URL.replace('/api', '') + '/swagger/v1/swagger.json';
      const response = await fetch(swaggerUrl);
      if (!response.ok) throw new Error(`Swagger não acessível: ${response.status}`);
      const spec = await response.json();
      setContractResults(validateContracts(spec));
    } catch (err) {
      setContractError(err.message);
    } finally {
      setContractLoading(false);
    }
  };

  // --- Stats ---
  const stats = stepResults.reduce(
    (acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; },
    { [STEP_STATUS.PENDING]: 0, [STEP_STATUS.SUCCESS]: 0, [STEP_STATUS.FAIL]: 0, [STEP_STATUS.RUNNING]: 0 }
  );
  const completedCount = stats[STEP_STATUS.SUCCESS] + stats[STEP_STATUS.FAIL];
  const progressPct = ALL_TEST_STEPS.length > 0 ? (completedCount / ALL_TEST_STEPS.length) * 100 : 0;

  const phaseGroups = {};
  ALL_TEST_STEPS.forEach((step, idx) => {
    if (!phaseGroups[step.phase]) phaseGroups[step.phase] = { name: PHASE_NAMES[step.phase], steps: [] };
    phaseGroups[step.phase].steps.push({ step, idx, result: stepResults[idx] });
  });

  const isIdle = runStatus === STATUS.IDLE || runStatus === STATUS.DONE || runStatus === STATUS.STOPPED;

  return (
    <Box className="api-health-container">
      {/* Header */}
      <Box className="api-health-header">
        <Box>
          <Typography variant="h6" fontWeight="bold" sx={{ lineHeight: 1.2 }}>API Health Check</Typography>
          <Typography variant="caption" color="text.secondary">
            {ALL_TEST_STEPS.length} steps | Para ao encontrar erro
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Reseta o usuário atual (renomeia email) e faz logoff. Usuário fica 'novo' sem dados.">
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<ResetIcon />}
              onClick={() => setResetDialogOpen(true)}
            >
              Reset User
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {/* Tabs */}
      <Box className="api-health-tabs">
        <button className={`api-health-tab ${activeTab === 'tests' ? 'active' : ''}`}
          onClick={() => setActiveTab('tests')} style={{ color: theme.palette.text.primary }}>
          Testes E2E
        </button>
        <button className={`api-health-tab ${activeTab === 'contracts' ? 'active' : ''}`}
          onClick={() => setActiveTab('contracts')} style={{ color: theme.palette.text.primary }}>
          Contratos
        </button>
      </Box>

      {/* === TAB: TESTS === */}
      {activeTab === 'tests' && (
        <>
          {/* Controls */}
          <Box className="api-health-controls" sx={{ mb: 1 }}>
            {isIdle ? (
              <Button variant="contained" size="small" startIcon={<PlayIcon />} onClick={handleStart}>
                {runStatus === STATUS.IDLE ? 'Iniciar' : 'Reiniciar'}
              </Button>
            ) : runStatus === STATUS.RUNNING ? (
              <Button variant="outlined" size="small" color="warning" startIcon={<PauseIcon />} onClick={handlePause}>
                Pausar
              </Button>
            ) : runStatus === STATUS.PAUSED ? (
              <>
                <Button variant="contained" size="small" color="success" startIcon={<PlayIcon />} onClick={handleResume}>Continuar</Button>
                <Button variant="outlined" size="small" color="error" startIcon={<StopIcon />} onClick={handleStop}>Parar</Button>
              </>
            ) : null}

            {failedAtIndex != null && isIdle && (
              <Button variant="outlined" size="small" color="warning" startIcon={<RetryIcon />}
                onClick={() => handleContinueFromStep(failedAtIndex)}>
                Retry do step {failedAtIndex + 1}
              </Button>
            )}
          </Box>

          {/* Stopped alert */}
          {runStatus === STATUS.STOPPED && failedAtIndex != null && (
            <Alert severity="error" className="stopped-alert" variant="outlined" sx={{ py: 0, fontSize: '0.8rem' }}>
              Parou no step {failedAtIndex + 1}: {ALL_TEST_STEPS[failedAtIndex]?.name}
            </Alert>
          )}

          {/* Progress bar */}
          {runStatus !== STATUS.IDLE && (
            <Box className="progress-bar-container" sx={{ bgcolor: alpha(theme.palette.divider, 0.3) }}>
              <Box className="progress-bar-fill" sx={{
                width: `${progressPct}%`,
                bgcolor: stats[STEP_STATUS.FAIL] > 0 ? 'error.main' : 'success.main',
              }} />
            </Box>
          )}

          {/* Summary */}
          {runStatus !== STATUS.IDLE && (
            <Box className="api-health-summary">
              <Paper className="summary-card" sx={{ bgcolor: alpha(theme.palette.info.main, 0.1) }}>
                <div className="count" style={{ color: theme.palette.info.main }}>{ALL_TEST_STEPS.length}</div>
                <div className="label">Total</div>
              </Paper>
              <Paper className="summary-card" sx={{ bgcolor: alpha(theme.palette.success.main, 0.1) }}>
                <div className="count" style={{ color: theme.palette.success.main }}>{stats[STEP_STATUS.SUCCESS]}</div>
                <div className="label">OK</div>
              </Paper>
              <Paper className="summary-card" sx={{ bgcolor: alpha(theme.palette.error.main, 0.1) }}>
                <div className="count" style={{ color: theme.palette.error.main }}>{stats[STEP_STATUS.FAIL]}</div>
                <div className="label">Fail</div>
              </Paper>
              <Paper className="summary-card" sx={{ bgcolor: alpha(theme.palette.text.disabled, 0.08) }}>
                <div className="count" style={{ color: theme.palette.text.secondary }}>{stats[STEP_STATUS.PENDING]}</div>
                <div className="label">Pend.</div>
              </Paper>
            </Box>
          )}

          {/* Phases */}
          {Object.entries(phaseGroups).map(([phase, group]) => {
            const phaseNum = parseInt(phase);
            const ps = group.steps.reduce((a, { result }) => { a[result.status] = (a[result.status] || 0) + 1; return a; }, {});
            const isExpanded = expandedPhases[phaseNum];

            return (
              <Box key={phase} className="phase-section">
                <Paper className="phase-header" onClick={() => togglePhase(phaseNum)}
                  sx={{ bgcolor: alpha(theme.palette.primary.main, 0.06) }}>
                  {isExpanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
                  <span className="phase-title">{phaseNum}. {group.name}</span>
                  <span className="phase-stats">
                    {ps[STEP_STATUS.SUCCESS] > 0 && <Chip label={`${ps[STEP_STATUS.SUCCESS]}ok`} size="small" color="success" variant="outlined" sx={{ mx: 0.2, height: 18, fontSize: '0.65rem' }} />}
                    {ps[STEP_STATUS.FAIL] > 0 && <Chip label={`${ps[STEP_STATUS.FAIL]}fail`} size="small" color="error" variant="outlined" sx={{ mx: 0.2, height: 18, fontSize: '0.65rem' }} />}
                    <Chip label={group.steps.length} size="small" variant="outlined" sx={{ mx: 0.2, height: 18, fontSize: '0.65rem' }} />
                  </span>
                </Paper>

                {isExpanded && (
                  <Box className="step-list">
                    {group.steps.map(({ step, idx, result }) => (
                      <Paper key={step.id} className="step-row" elevation={0}
                        sx={{
                          bgcolor: result.status === STEP_STATUS.FAIL ? alpha(theme.palette.error.main, 0.05)
                            : result.status === STEP_STATUS.SUCCESS ? alpha(theme.palette.success.main, 0.02) : 'transparent',
                        }}>
                        <Box className="step-status-icon">{getStatusIcon(result.status)}</Box>
                        <Box className="step-info">
                          <Tooltip title={`${step.service}.${step.method}() — ${step.description || ''}`} arrow enterDelay={400}>
                            <Box className="step-name">{step.name}</Box>
                          </Tooltip>
                          <Box className="step-service">{step.service}.{step.method}()</Box>

                          {result.errors.length > 0 && (
                            <Box className="step-errors" sx={{ bgcolor: alpha(theme.palette.error.main, 0.08), color: theme.palette.error.dark }}>
                              <ul style={{ margin: 0, paddingLeft: 16 }}>
                                {result.errors.map((e, i) => <li key={i}>{e}</li>)}
                              </ul>
                            </Box>
                          )}

                          {result.requestData?.length > 0 && (
                            <>
                              <span className="step-response-toggle" onClick={() => toggleRequest(step.id)}
                                style={{ color: theme.palette.info.main }}>
                                {expandedRequests[step.id] ? 'ocultar req' : 'request'}
                              </span>
                              {expandedRequests[step.id] && (
                                <Box className="step-response-data" sx={{ bgcolor: alpha(theme.palette.info.main, 0.04), color: theme.palette.text.secondary }}>
                                  {JSON.stringify(result.requestData.length === 1 ? result.requestData[0] : result.requestData, null, 2)}
                                </Box>
                              )}
                            </>
                          )}

                          {(result.status === STEP_STATUS.SUCCESS || result.status === STEP_STATUS.FAIL) && (
                            <>
                              <span className="step-response-toggle" onClick={() => toggleResponse(step.id)}>
                                {expandedResponses[step.id] ? 'ocultar' : 'response'}
                              </span>
                              {expandedResponses[step.id] && (
                                <Box className="step-response-data" sx={{ bgcolor: alpha(theme.palette.text.primary, 0.04), color: theme.palette.text.secondary }}>
                                  {result.responseData !== null && result.responseData !== undefined
                                    ? JSON.stringify(result.responseData, null, 2)
                                    : '204 No Content'}
                                </Box>
                              )}
                            </>
                          )}
                        </Box>

                        {result.duration != null && <Box className="step-duration">{result.duration}ms</Box>}

                        {result.status === STEP_STATUS.FAIL && isIdle && (
                          <Box sx={{ display: 'flex', gap: 0.3 }}>
                            <Tooltip title="Retry este step">
                              <button className="step-retry-btn" onClick={() => handleRetryStep(idx)}
                                style={{ borderColor: theme.palette.warning.main, color: theme.palette.warning.main }}>
                                <RetryIcon sx={{ fontSize: 12, verticalAlign: 'text-bottom' }} />
                              </button>
                            </Tooltip>
                            <Tooltip title="Continuar daqui">
                              <button className="step-retry-btn" onClick={() => handleContinueFromStep(idx)}
                                style={{ borderColor: theme.palette.info.main, color: theme.palette.info.main }}>
                                <PlayIcon sx={{ fontSize: 12, verticalAlign: 'text-bottom' }} />
                              </button>
                            </Tooltip>
                          </Box>
                        )}
                      </Paper>
                    ))}
                  </Box>
                )}
              </Box>
            );
          })}
        </>
      )}

      {/* === TAB: CONTRACTS === */}
      {activeTab === 'contracts' && (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Compara services do Admin com Swagger/OpenAPI da API.
          </Typography>

          <Button variant="contained" size="small" onClick={handleLoadContracts} disabled={contractLoading}
            startIcon={contractLoading ? <span className="spinning"><RunningIcon /></span> : <ContractIcon />} sx={{ mb: 1 }}>
            {contractLoading ? 'Carregando...' : 'Validar Contratos'}
          </Button>

          {contractError && <Alert severity="error" sx={{ mb: 1, py: 0, fontSize: '0.8rem' }}>{contractError}</Alert>}

          {contractResults && (
            <>
              <Box className="api-health-summary" sx={{ mb: 1 }}>
                <Paper className="summary-card" sx={{ bgcolor: alpha(theme.palette.success.main, 0.1) }}>
                  <div className="count" style={{ color: theme.palette.success.main }}>{contractResults.summary.matched}</div>
                  <div className="label">OK</div>
                </Paper>
                <Paper className="summary-card" sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1) }}>
                  <div className="count" style={{ color: theme.palette.warning.main }}>{contractResults.summary.mismatched}</div>
                  <div className="label">Diverg.</div>
                </Paper>
                <Paper className="summary-card" sx={{ bgcolor: alpha(theme.palette.error.main, 0.1) }}>
                  <div className="count" style={{ color: theme.palette.error.main }}>{contractResults.summary.missing}</div>
                  <div className="label">Ausentes</div>
                </Paper>
              </Box>

              {contractResults.results.map((r, i) => (
                <Paper key={i} className="contract-row" elevation={0}
                  sx={{ bgcolor: r.status === 'ok' ? alpha(theme.palette.success.main, 0.04)
                    : r.status === 'missing' ? alpha(theme.palette.error.main, 0.06) : alpha(theme.palette.warning.main, 0.06) }}>
                  <Box className="step-status-icon">
                    {r.status === 'ok' && <CheckIcon sx={{ color: 'success.main', fontSize: 18 }} />}
                    {r.status === 'missing' && <ErrorIcon sx={{ color: 'error.main', fontSize: 18 }} />}
                    {r.status === 'mismatch' && <ErrorIcon sx={{ color: 'warning.main', fontSize: 18 }} />}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ fontWeight: 500, fontSize: '0.82rem' }}>
                      <Chip label={r.httpMethod} size="small" sx={{ mr: 0.5, height: 16, fontSize: '0.65rem', fontWeight: 600 }} />
                      {r.path}
                    </Box>
                    {r.issues.length > 0 && (
                      <Box className="contract-issues" sx={{ color: r.status === 'missing' ? 'error.main' : 'warning.dark' }}>
                        <ul style={{ margin: 0, paddingLeft: 16 }}>{r.issues.map((issue, j) => <li key={j}>{issue}</li>)}</ul>
                      </Box>
                    )}
                  </Box>
                </Paper>
              ))}
            </>
          )}
        </Box>
      )}

      {/* Reset User Dialog */}
      <Dialog open={resetDialogOpen} onClose={() => !resetLoading && setResetDialogOpen(false)}>
        <DialogTitle sx={{ pb: 1 }}>Reset de Usuário</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '0.85rem' }}>
            Esta ação irá renomear o email do seu usuário para <strong>email-deleted_at_timestamp</strong> e fazer logoff.
            Ao entrar novamente, será um usuário novo, sem dados.
            <br /><br />
            <strong>Apenas usuários de teste são permitidos.</strong>
          </DialogContentText>
          {resetError && <Alert severity="error" sx={{ mt: 1, py: 0, fontSize: '0.8rem' }}>{resetError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)} disabled={resetLoading}>Cancelar</Button>
          <Button onClick={handleResetUser} color="error" variant="contained" disabled={resetLoading}
            startIcon={resetLoading ? <span className="spinning"><RunningIcon sx={{ fontSize: 16 }} /></span> : <ResetIcon />}>
            {resetLoading ? 'Resetando...' : 'Confirmar Reset'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
